import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import VMSNav from '@components/dashboard/components/VMSNav';
import { NavContext } from '@components/navigation/Context';
import { AnimationContainer, FullPageContainer } from '@components/navigation/NavAnimation';
import Colors from '@components/shared/Colors';
import useDesktop from '@components/shared/useDesktop';
import { HAMBURGER_BREAKPOINT } from '@constants';
import { NavBarCategory, NavStatus } from '@contracts';
import { getNavigationData } from '@data/navigation';
import { pageIsAccountPage } from '@utils/helpers';
import NavMainMenu from './NavMainMenu';

const NavMenu: React.FC = () => {
  const { navState, navDispatch } = useContext(NavContext);
  const { status, showingBanner, navbarSelectedCategories } = navState;
  const [showContainer, setShowContainer] = useState(false);
  const isDesktop = useDesktop(HAMBURGER_BREAKPOINT);
  const isSearch = navbarSelectedCategories?.length ? navbarSelectedCategories.includes(NavBarCategory.Search) : false;
  const {
    periodCare,
    vaginalHealth,
    accessories,
    aboutUs,
    discover,
    productCategories,
    vitalsArticleUids,
    policies: { items: policiesMenuItems }
  } = getNavigationData();
  const location = useLocation();
  const isAccountPage = pageIsAccountPage(location.pathname);
  const menuWidth = isAccountPage ? '350px' : '100%';
  const menuHeight = !isSearch && (isAccountPage || !isDesktop) ? '100%' : 'auto';
  const [animateIn, setAnimateIn] = useState(false);

  const handleCloseNav = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    navDispatch({ type: 'removeNavbarSelectedCategories' });
  };

  const handleMouseLeave = (e) => {
    if (
      isSearch ||
      !e.relatedTarget ||
      e.relatedTarget === window ||
      e.relatedTarget === document ||
      e.relatedTarget instanceof Window ||
      e.relatedTarget.closest('#nav-bar') ||
      e.relatedTarget.closest('.aa-Panel')
    ) {
      return;
    }

    handleCloseNav();
  };

  useEffect(() => {
    if (status === NavStatus.Menu) {
      setTimeout(() => {
        setShowContainer(true);
        setAnimateIn(true);
      }, 200);
    } else {
      setAnimateIn(false);
      setTimeout(() => {
        setShowContainer(false);
      }, 200);
    }
  }, [status]);
  const isWindowScrollTop = typeof window === 'undefined' || window?.scrollY === 0;

  return (
    <FullPageContainer $type={isAccountPage ? 'vms-menu' : ''} $open={showContainer} $animateIn={animateIn} onClick={handleCloseNav}>
      <AnimationContainer $open={animateIn} $width={menuWidth} className="nav-menu-container">
        <Container
          $type={isAccountPage ? 'vms-menu' : ''}
          onClick={(e) => e.stopPropagation()}
          id="nav-menu"
          $width={menuWidth}
          $height={menuHeight}
          $hasBanner={showingBanner && isWindowScrollTop}
          onMouseLeave={handleMouseLeave}
        >
          {isAccountPage ? (
            <VMSNav />
          ) : (
            <NavMainMenu
              policiesMenuItems={policiesMenuItems}
              periodCare={periodCare}
              vaginalHealth={vaginalHealth}
              accessories={accessories}
              aboutUs={aboutUs}
              discover={discover}
              productCategories={productCategories}
              vitalsArticleUids={vitalsArticleUids}
            />
          )}
        </Container>
      </AnimationContainer>
    </FullPageContainer>
  );
};

export default NavMenu;

interface ContainerProps {
  $width: string;
  $height: string;
  $type?: string;
  $hasBanner?: boolean;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${Colors.cream};
  padding: ${(props) => (props.$hasBanner ? '119px 0 60px' : '69px 0 60px')};
  width: ${(props) => props.$width};
  min-height: ${(props) => props.$height};
  height: ${(props) => props.$height};

  @media (max-width: 768px) {
    padding: ${(props) => (props.$type === 'vms-menu' ? '0 0 100px' : `${props.$hasBanner ? 119 : 69}px 0 0`)};
    width: 100%;
  }
`;
